import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import JsonViewer from "./JsonViewer";

interface SecureMarkdownRendererProps {
    content: string | object;
    className?: string;
}

export function SecureMarkdownRenderer({ content, className = '' }: SecureMarkdownRendererProps) {
    // Convert content to string if needed
    const displayContent = useMemo(() => {
        if (typeof content === 'object') {
            return JSON.stringify(content, null, 2);
        }
        return content || '';
    }, [content]);

    // Custom renderer for code blocks
    const components = useMemo(() => ({
        code({ inline, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1].toLowerCase() : '';
            const codeString = String(children).replace(/\n$/, '');

            // Inline code
            if (inline) {
                return (
                    <code
                        style={{
                            backgroundColor: '#f3f4f6',
                            color: '#1f2937',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.875em',
                            fontFamily: 'monospace',
                        }}
                    >
                        {children}
                    </code>
                );
            }

            // JSON code blocks - render with JsonViewer
            if (language === 'json') {
                try {
                    const jsonData = JSON.parse(codeString);
                    return (
                        <div style={{margin: '16px 0'}}>
                            <JsonViewer data={jsonData}/>
                        </div>
                    );
                } catch (e) {
                    // If parsing fails, fall through to render as regular code block
                }
            }

            // Default code block
            return (
                <pre style={{
                    backgroundColor: '#f9fafb',
                    color: '#1f2937',
                    padding: '16px',
                    borderRadius: '8px',
                    overflow: 'auto',
                    margin: '16px 0',
                    border: '1px solid #e5e7eb',
                }}>
          <code
              style={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  color: '#1f2937',
              }}
          >
            {children}
          </code>
        </pre>
            );
        },

        // Headings
        h1: ({ children }: any) => (
            <h1 style={{
                fontSize: '1.875rem',
                fontWeight: '700',
                marginTop: '24px',
                marginBottom: '16px',
                lineHeight: '1.25',
            }}>
                {children}
            </h1>
        ),
        h2: ({ children }: any) => (
            <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginTop: '20px',
                marginBottom: '12px',
                lineHeight: '1.3',
            }}>
                {children}
            </h2>
        ),
        h3: ({ children }: any) => (
            <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                marginTop: '16px',
                marginBottom: '10px',
                lineHeight: '1.4',
            }}>
                {children}
            </h3>
        ),

        // Paragraphs
        p: ({ children }: any) => (
            <p style={{
                fontSize: '1rem',
                lineHeight: '1.75',
                marginBottom: '16px',
            }}>
                {children}
            </p>
        ),

        // Lists
        ul: ({ children }: any) => (
            <ul style={{
                marginLeft: '24px',
                marginBottom: '16px',
                listStyleType: 'disc',
            }}>
                {children}
            </ul>
        ),
        ol: ({ children }: any) => (
            <ol style={{
                marginLeft: '24px',
                marginBottom: '16px',
                listStyleType: 'decimal',
            }}>
                {children}
            </ol>
        ),
        li: ({ children }: any) => (
            <li style={{
                fontSize: '1rem',
                lineHeight: '1.75',
                marginBottom: '8px',
            }}>
                {children}
            </li>
        ),

        // Links
        a: ({ href, children }: any) => (
            <a
                href={href}
                style={{
                    color: '#60a5fa',
                    textDecoration: 'none',
                }}
                onMouseOver={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
                onMouseOut={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
            >
                {children}
            </a>
        ),

        // Blockquote
        blockquote: ({ children }: any) => (
            <blockquote style={{
                borderLeft: '4px solid #d1d5db',
                paddingLeft: '16px',
                marginLeft: '0',
                marginBottom: '16px',
                fontStyle: 'italic',
                color: '#6b7280',
            }}>
                {children}
            </blockquote>
        ),

        // Strong/Bold
        strong: ({ children }: any) => (
            <strong style={{ fontWeight: '600' }}>
                {children}
            </strong>
        ),

        // Table
        table: ({ children }: any) => (
            <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    border: '1px solid #e5e7eb',
                }}>
                    {children}
                </table>
            </div>
        ),
        th: ({ children }: any) => (
            <th style={{
                padding: '12px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                fontWeight: '600',
                textAlign: 'left',
                color: '#1f2937',
            }}>
                {children}
            </th>
        ),
        td: ({ children }: any) => (
            <td style={{
                padding: '12px',
                border: '1px solid #e5e7eb',
                color: '#374151',
            }}>
                {children}
            </td>
        ),

        // Horizontal rule
        hr: () => (
            <hr style={{
                border: 'none',
                borderTop: '1px solid #e5e7eb',
                margin: '24px 0',
            }} />
        ),
    }), []);

    return (
        <div
            className={className}
            style={{
                fontSize: '1rem',
                lineHeight: '1.75',
                maxWidth: '100%',
                wordBreak: 'break-word',
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components}
            >
                {displayContent}
            </ReactMarkdown>
        </div>
    );
}

export default SecureMarkdownRenderer;